/**
 * WHAT: SQL Server group-based access report showing Windows/AD groups and their effective privileges.
 * WHY:  MSSQL access is often granted via security groups rather than individual users; this report
 *       lets compliance teams audit group-level privilege assignments and drill into group membership
 *       to understand who inherits access through group membership.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import './MySQLUserAccessReport.css'; // Reuse MySQL styles

/**
 * WHAT: Renders a filterable, sortable report of MSSQL security groups with privilege levels and member details.
 * WHY:  Enables auditors to evaluate group-based access patterns on SQL Server, including group membership
 *       drilldowns, to ensure least-privilege compliance and identify over-privileged groups.
 */
const MSSQLGroupAccessReport = () => {
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [groups, setGroups] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    privilege_level: '',
    search: ''
  });
  const [sortBy, setSortBy] = useState('privilege_level');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState({ title: '', items: [], type: '' });
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [membershipData, setMembershipData] = useState({ groupName: '', members: [], loading: false });

  // Privilege-level filter options driven by the Privilege Matrix per-tech config.
  const [activeTiers, setActiveTiers] = useState([]);

  // Certification status for the MSSQL privilege mappings. If any privilege or
  // fixed role is UNASSIGNED or uncertified, computed group privilege levels
  // here may be understated — surfaced as a top-of-report warning.
  const [certStatus, setCertStatus] = useState(null);

  // Fetch connections on component mount
  useEffect(() => {
    fetchConnections();
    fetchActiveTiers();
    fetchCertificationStatus();
  }, []);

  // Same endpoint the Manage Privilege Mappings / User report banner uses, so
  // the numbers are consistent. Best-effort: a failure just hides the warning.
  const fetchCertificationStatus = async () => {
    try {
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/check-certification/'),
        { params: { database_type: 'MSSQL' } }
      );
      if (response.data?.status === 'success') {
        setCertStatus(response.data);
      }
    } catch (e) {
      setCertStatus(null);
    }
  };

  // Client-side CSV export of the currently loaded group rows. The Group
  // endpoint returns the full filtered set (no server pagination), so the
  // export matches what the user is viewing.
  const exportToCSV = () => {
    if (!groups.length) return;
    const cols = [
      ['Group Name', g => g.principal_name],
      ['Group Type', g => g.principal_type || ''],
      ['Server Permissions', g => g.direct_server_permission_count || 0],
      ['Database Permissions', g => g.direct_database_permission_count || 0],
      ['Server Roles', g => g.server_role_count || 0],
      ['Database Roles', g => g.database_role_count || 0],
      ['Privilege Level', g => g.privilege_level_name || 'NONE'],
      ['Disabled', g => (g.is_disabled ? 'Yes' : 'No')],
    ];
    const esc = v => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [
      cols.map(c => c[0]).join(','),
      ...groups.map(g => cols.map(c => esc(c[1](g))).join(',')),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mssql_group_access_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchActiveTiers = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-matrix/mssql/active-tiers/'));
      if (response.data?.success) {
        setActiveTiers(response.data.tiers || []);
      }
    } catch (err) {
      console.error('Failed to fetch active tiers for MSSQL:', err);
    }
  };

  const fetchConnections = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('mssql/source-connections/'));
      const mssqlConnections = response.data.connections || [];
      // Map id to connection_id for consistency
      const mappedConns = mssqlConnections.map(conn => ({
        ...conn,
        connection_id: conn.id,
        database_type: 'MSSQL'
      }));
      setConnections(mappedConns);
      if (mappedConns.length > 0) {
        setSelectedConnection(mappedConns[0].connection_id);
      }
    } catch (err) {
      setError('Failed to load database connections');
      console.error(err);
    }
  };

  // Fetch group data when connection or filters change
  useEffect(() => {
    if (selectedConnection) {
      fetchGroupData();
    }
  }, [selectedConnection, filters, sortBy, sortOrder]);

  const fetchGroupData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('mssql-group-access-report/'), {
        connection_id: selectedConnection,
        filters: filters,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (response.data.success) {
        setGroups(response.data.groups || []);
        setStatistics(response.data.statistics || {});
      } else {
        setError(response.data.error || 'Failed to fetch group data');
      }
    } catch (err) {
      setError('Error fetching group access data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectionChange = (e) => {
    setSelectedConnection(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const showRoleDetails = (group, type) => {
    let title = '';
    let items = [];

    if (type === 'server') {
      title = `Server Roles for ${group.principal_name}`;
      items = group.server_roles || [];
    } else if (type === 'database') {
      title = `Database Roles for ${group.principal_name}`;
      items = group.database_roles || [];
    }

    setDetailModalData({ title, items, type: 'role' });
    setShowDetailModal(true);
  };

  const fetchGroupMembership = async (groupName) => {
    setMembershipData({ groupName, members: [], loading: true });
    setShowMembershipModal(true);

    try {
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('mssql-group-membership/'), {
        connection_id: selectedConnection,
        group_name: groupName
      });

      if (response.data.success) {
        setMembershipData({
          groupName,
          members: response.data.members || [],
          loading: false
        });
      } else {
        setMembershipData({
          groupName,
          members: [],
          loading: false,
          error: response.data.error || 'Failed to fetch group membership'
        });
      }
    } catch (err) {
      console.error('Error fetching group membership:', err);
      setMembershipData({
        groupName,
        members: [],
        loading: false,
        error: 'Error fetching group membership'
      });
    }
  };

  const showPermissionDetails = (group, type) => {
    let title = '';
    let items = [];

    if (type === 'server') {
      title = `Server Permissions for ${group.principal_name}`;
      items = group.server_permissions || [];
    } else if (type === 'database') {
      title = `Database Permissions for ${group.principal_name}`;
      items = group.database_permissions || [];
    }

    setDetailModalData({ title, items, type: 'permission' });
    setShowDetailModal(true);
  };

  const getRiskBadgeClass = (level) => {
    if (level >= 6) return 'badge-critical';
    if (level >= 4) return 'badge-high';
    if (level >= 2) return 'badge-medium';
    return 'badge-low';
  };

  // Pagination
  const totalPages = Math.ceil(groups.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentGroups = groups.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="mysql-access-report">
      <div className="report-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <h2>SQL Server Group Access Report</h2>
          <p className="report-subtitle">Windows security groups and their SQL Server permissions</p>
        </div>
        <button className="btn btn-export" onClick={exportToCSV} disabled={!groups.length} style={{ flexShrink: 0 }}>📊 Export to CSV</button>
      </div>

      {/* Privilege-mapping completeness warning. Shown when any MSSQL
          privilege or fixed role is UNASSIGNED or uncertified — those
          mappings contribute nothing to a group's computed level, so the
          levels below may be understated. Same source as the User report. */}
      {certStatus && (() => {
        const unassigned = (certStatus.unassigned_count || 0);
        const uncertPriv = (certStatus.uncertified_count || 0);
        const uncertRole = (certStatus.role_uncertified_count || 0);
        const incomplete = unassigned + uncertPriv + uncertRole;
        if (incomplete === 0) return null;
        return (
          <div style={{
            background: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ color: '#856404', fontSize: '0.95rem', lineHeight: 1.5 }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
              <strong>Privilege levels below may be understated.</strong>
              {' '}The MSSQL privilege mappings are incomplete:
              {unassigned > 0 && <> <strong>{unassigned}</strong> unassigned privilege{unassigned === 1 ? '' : 's'}</>}
              {unassigned > 0 && (uncertPriv > 0 || uncertRole > 0) ? ',' : ''}
              {uncertPriv > 0 && <> <strong>{uncertPriv}</strong> uncertified privilege{uncertPriv === 1 ? '' : 's'}</>}
              {uncertPriv > 0 && uncertRole > 0 ? ',' : ''}
              {uncertRole > 0 && <> <strong>{uncertRole}</strong> uncertified role{uncertRole === 1 ? '' : 's'}</>}.
              {' '}Unassigned or uncertified mappings do not contribute to a group's
              computed privilege level until a DBA classifies and certifies them in{' '}
              <strong>Manage Privilege Mappings</strong>, so the levels shown here
              may be lower than a group's effective access.
            </div>
          </div>
        );
      })()}

      {/* Compact summary cards — scoped override only for this report so the
          shared .summary-card (MySQL/Oracle/etc.) is untouched. */}
      <style>{`
        .mssql-compact-cards {
          display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px;
        }
        .mssql-compact-cards .summary-card {
          flex: 1 1 96px; padding: 6px 10px; border-radius: 7px;
          box-shadow: none; transition: none;
          display: flex; align-items: baseline; justify-content: space-between;
          gap: 8px;
        }
        .mssql-compact-cards .summary-card:hover { transform: none; box-shadow: none; }
        .mssql-compact-cards .summary-label {
          font-size: 10.5px; margin-bottom: 0; white-space: nowrap;
        }
        .mssql-compact-cards .summary-value { font-size: 17px; }
      `}</style>
      {(() => {
        // Dynamic privilege-level cards: one per DISTINCT level actually
        // present among groups (count > 0), severity high→low. Total Groups
        // stays as a fixed context card. Levels with 0 groups are omitted.
        const SEVERITY_ORDER = [
          ['SUPER_ADMIN', 'danger'],
          ['ADMIN', 'danger'],
          ['WRITE_ELEVATED', 'warning'],
          ['WRITE', 'warning'],
          ['READ_ELEVATED', 'info'],
          ['READ', 'info'],
          ['NONE', ''],
          ['UNASSIGNED', ''],
        ];
        const dist = statistics.privilege_distribution || {};
        const levelCards = SEVERITY_ORDER
          .filter(([name]) => (dist[name] || 0) > 0)
          .map(([name, cls]) => ({ name, cls, count: dist[name] }));
        return (
          <div className="summary-cards mssql-compact-cards">
            <div className="summary-card">
              <div className="summary-label">Total Groups</div>
              <div className="summary-value">{statistics.total_groups || 0}</div>
            </div>
            {levelCards.map(c => (
              <div key={c.name} className={`summary-card ${c.cls}`}>
                <div className="summary-label">{c.name.replace(/_/g, ' ')}</div>
                <div className="summary-value">{c.count}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Filters */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>SQL Server Instance</label>
            <select value={selectedConnection} onChange={handleConnectionChange}>
              <option value="">Select Connection</option>
              {connections.map(conn => (
                <option key={conn.connection_id} value={conn.connection_id}>
                  {conn.database_name || conn.connection_name} - {conn.host || conn.db_host}:{conn.port || conn.db_port}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Privilege Level</label>
            <select name="privilege_level" value={filters.privilege_level} onChange={handleFilterChange}>
              <option value="">All Levels</option>
              {activeTiers.map(t => (
                <option key={t.tier_name} value={t.tier_value}>{t.tier_name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search group names..."
            />
          </div>
        </div>
      </div>

      {/* Groups Table */}
      {loading ? (
        <div className="loading">Loading group data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="table-container">
          <div className="table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('principal_name')}>Group Name</th>
                  <th>Group Type</th>
                  <th onClick={() => handleSort('direct_server_permission_count')}>Server Permissions</th>
                  <th onClick={() => handleSort('direct_database_permission_count')}>Database Permissions</th>
                  <th onClick={() => handleSort('server_role_count')}>Server Roles</th>
                  <th onClick={() => handleSort('database_role_count')}>Database Roles</th>
                  <th onClick={() => handleSort('privilege_level')}>Privilege Level</th>
                  <th>Risk Assessment</th>
                </tr>
              </thead>
              <tbody>
                {currentGroups.map((group, index) => {
                  const isHighRisk = group.privilege_level >= 5;
                  return (
                    <tr key={index} className={isHighRisk ? 'critical-row' : ''}>
                      <td className="username-cell">
                        <a
                          href="#"
                          onClick={(e) => { e.preventDefault(); fetchGroupMembership(group.principal_name); }}
                          style={{
                            color: '#2563eb',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                          title="Click to view group members"
                        >
                          {group.principal_name}
                        </a>
                        <span style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          border: '1px solid #fbbf24'
                        }}>
                          WINDOWS GROUP
                        </span>
                      </td>
                      <td>{group.principal_type || 'N/A'}</td>
                      <td>
                        {(group.direct_server_permission_count || 0) > 0 ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); showPermissionDetails(group, 'server'); }}
                             style={{ color: '#059669', textDecoration: 'underline', cursor: 'pointer' }}>
                            {group.direct_server_permission_count || 0}
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>0</span>
                        )}
                      </td>
                      <td>
                        {(group.direct_database_permission_count || 0) > 0 ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); showPermissionDetails(group, 'database'); }}
                             style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}>
                            {group.direct_database_permission_count || 0}
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>0</span>
                        )}
                      </td>
                      <td>
                        {(group.server_role_count || 0) > 0 ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); showRoleDetails(group, 'server'); }}
                             style={{ color: '#dc2626', textDecoration: 'underline', cursor: 'pointer' }}>
                            {group.server_role_count || 0}
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>0</span>
                        )}
                      </td>
                      <td>
                        {(group.database_role_count || 0) > 0 ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); showRoleDetails(group, 'database'); }}
                             style={{ color: '#059669', textDecoration: 'underline', cursor: 'pointer' }}>
                            {group.database_role_count || 0}
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>0</span>
                        )}
                      </td>
                      <td>
                        <span className={getRiskBadgeClass(group.privilege_level)}>
                          {group.privilege_level_name || 'NONE'}
                        </span>
                      </td>
                      <td>
                        <span className={getRiskBadgeClass(group.privilege_level)}>
                          {group.privilege_level >= 6 ? 'CRITICAL' :
                           group.privilege_level >= 4 ? 'HIGH' :
                           group.privilege_level >= 2 ? 'MEDIUM' : 'LOW'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {groups.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '20px',
              padding: '15px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label>Rows per page:</label>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span style={{ marginLeft: '20px' }}>
                  Showing {startIndex + 1}-{Math.min(endIndex, groups.length)} of {groups.length}
                </span>
              </div>

              <div style={{ display: 'flex', gap: '5px' }}>
                <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
                <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                <span style={{ padding: '0 10px', alignSelf: 'center' }}>Page {currentPage} of {totalPages}</span>
                <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Last</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '10px'
            }}>
              <h3 style={{ margin: 0, color: '#1f2937' }}>{detailModalData.title}</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            {detailModalData.items.length === 0 ? (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                No {detailModalData.type === 'permission' ? 'permissions' : 'roles'} found.
              </p>
            ) : (
              <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                {detailModalData.type === 'permission' ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Permission</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>State</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Scope</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailModalData.items.map((perm, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '500' }}>{perm.permission_name || perm}</td>
                          <td style={{ padding: '8px' }}>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              backgroundColor: perm.state === 'GRANT' ? '#d1fae5' : perm.state === 'DENY' ? '#fee2e2' : '#fef3c7',
                              color: perm.state === 'GRANT' ? '#065f46' : perm.state === 'DENY' ? '#991b1b' : '#92400e'
                            }}>
                              {perm.state || 'GRANT'}
                            </span>
                          </td>
                          <td style={{ padding: '8px', color: '#6b7280' }}>{perm.scope || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Role Name</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailModalData.items.map((role, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '500' }}>{role.role_name || role}</td>
                          <td style={{ padding: '8px' }}>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              backgroundColor: role.is_fixed_role ? '#dbeafe' : '#f3f4f6',
                              color: role.is_fixed_role ? '#1e40af' : '#374151'
                            }}>
                              {role.is_fixed_role ? 'Fixed Role' : 'Custom Role'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            <div style={{ marginTop: '15px', textAlign: 'right' }}>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Membership Modal */}
      {showMembershipModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '10px'
            }}>
              <h3 style={{ margin: 0, color: '#1f2937' }}>
                Group Members: {membershipData.groupName}
              </h3>
              <button
                onClick={() => setShowMembershipModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            {membershipData.loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                Loading group members...
              </div>
            ) : membershipData.error ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#dc2626' }}>
                {membershipData.error}
              </div>
            ) : membershipData.members.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                No members found for this group, or membership information is not available from SQL Server.
                <br /><br />
                <small style={{ color: '#9ca3af' }}>
                  Note: Windows group membership is managed in Active Directory and may not be directly queryable from SQL Server.
                </small>
              </div>
            ) : (
              <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                <div style={{ marginBottom: '10px', color: '#6b7280', fontSize: '14px' }}>
                  Found {membershipData.members.length} member(s)
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Member Name</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Member Type</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Status</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Privilege Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membershipData.members.map((member, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px', fontWeight: '500' }}>{member.principal_name || member.member_name}</td>
                        <td style={{ padding: '8px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            backgroundColor: '#dbeafe',
                            color: '#1e40af'
                          }}>
                            {member.principal_type || member.member_type || 'User'}
                          </span>
                        </td>
                        <td style={{ padding: '8px' }}>
                          <span style={{
                            color: member.is_disabled ? '#dc2626' : '#059669',
                            fontWeight: member.is_disabled ? 'bold' : 'normal'
                          }}>
                            {member.is_disabled ? 'DISABLED' : 'ENABLED'}
                          </span>
                        </td>
                        <td style={{ padding: '8px' }}>
                          <span className={getRiskBadgeClass(member.privilege_level || 0)}>
                            {member.privilege_level_name || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: '15px', textAlign: 'right' }}>
              <button
                onClick={() => setShowMembershipModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MSSQLGroupAccessReport;
